/**
 * RTL Scanner — tile normalization.
 *
 * Port of the RTL-scanner backend (`server/fetchReport.js`) into the frontend so the
 * scanner reads `GET /api/ois/report-pulse-summary-json` directly instead of going
 * through a separate Express process.
 *
 * DATA-INTEGRITY CONTRACT (preserved verbatim from the RTL-scanner README):
 *  - Zero fabrication, zero inference, zero recomputation. Every value shown must be
 *    traceable to a specific field in the upstream response. Missing/invalid values are
 *    rendered as `----` / `Not Available` — never guessed, estimated or filled in.
 *  - Only two derived values are permitted: moneyness (strike vs `underlying_spot_map`,
 *    `----` when |pct| > 60%) and colour = direction (not tape-side execution).
 *  - On any fetch/validation failure the last successfully validated report keeps being
 *    served (`STALE`); when nothing has ever validated the scanner reports `MAINTENANCE`
 *    rather than inventing a replacement.
 */

import { elitePulseFetch } from './elitePulseFetch';
import type { FlowVerdictLabels } from './flowVerdictLabels';
import {
  directionFromTapeSide,
  EMPTY_FLOW_VERDICT,
  structureFromConditionDisplay,
  verdictFromDetail,
} from './flowVerdictLabels';

/**
 * Every block of the report that names contracts.
 *
 * This was three keys and produced five tiles from a report carrying 567 opportunities
 * across seven blocks -- 350 of them distinct contracts. The three biggest were absent
 * entirely: `v91_magnet_strikes` (448), `critical_mass` (57) and `strike_precision` (57).
 * They were not excluded on purpose; they simply were not listed, and the parser also
 * rejected them for lacking `side`, `strike` or `expiry` as separate fields -- which every
 * one of them encodes in its OCC symbol anyway. Both halves of that are fixed here.
 */
export const IMBALANCE_LIST_KEYS = [
  'v91_whale_arch',
  'v91_institutional_hedging_vol_selling',
  'v91_mid_cap_gems',
  'v91_magnet_strikes',
  'critical_mass',
  'strike_precision',
] as const;

/**
 * The blocks a pulse report must contain to be one.
 *
 * Only the original three. Their absence means the payload is not the report -- which is how
 * a stripped or wrong-tier artifact gets caught. The rest are read when present and their
 * absence is unremarkable: a quiet session may have no magnet strikes, and the generator has
 * added blocks between versions before.
 */
export const REQUIRED_LIST_KEYS = [
  'v91_whale_arch',
  'v91_institutional_hedging_vol_selling',
  'v91_mid_cap_gems',
] as const;

/**
 * Ticker, expiry, side and strike from an OCC symbol.
 *
 * The blocks disagree about which of these they carry as separate fields -- one has strike
 * and expiry but no side, another has side and strike but no expiry, a third has none of
 * them -- while all four are encoded in the symbol every one of them does carry. Reading
 * them from there is what lets a single parser accept all six blocks.
 */
export function parseOccSymbol(
  raw: unknown,
): { ticker: string; expiry: string; side: TileSide; strike: number } | null {
  const token = String(raw ?? '').trim().toUpperCase().replace(/^O:/, '').replace(/\s+/g, '');
  const m = token.match(/^([A-Z]{1,6})(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/);
  if (!m) return null;
  const [, ticker, yy, mm, dd, side, strike8] = m;
  return {
    ticker,
    expiry: `20${yy}-${mm}-${dd}`,
    side: side as TileSide,
    strike: Number(strike8) / 1000,
  };
}

export type TileSide = 'C' | 'P';
export type TileFlow = 'BUY' | 'SELL' | 'UNK';
export type TileColor = 'buy' | 'sell' | 'unk';
export type MoneynessBucket = 'OTM' | 'ITM' | 'ATM' | 'NA';
export type ScannerStatus = 'LIVE' | 'STALE' | 'MAINTENANCE';

export interface ScannerTile {
  /** Ticker — first token of the OCC `symbol`. */
  t: string;
  /** `rel_vol` from source, or null when missing. */
  rv: number | null;
  side: TileSide;
  strike: number;
  /** Expiry exactly as given by source. */
  exp: string;
  /** Days to expiry relative to the report's own date (not wall-clock now). */
  dte: number | null;
  /** `premium` from source, or null when missing. */
  prem: number | null;
  flow: TileFlow;
  /** Moneyness label, e.g. `OTM 12%`, `ATM`, `ITM 5%`, or `----`. */
  money: string;
  mc: MoneynessBucket;
  /** Human-readable flow label: Buy Call / Buy Put / Sell Call / Sell Put / Unknown. */
  fl: string;
  color: TileColor;
  horizon_tml: string | null;
  /** Market-cap bucket as reported by the source, normalised by utils/capTier. */
  cap?: string;
  /**
   * `stock` / `etf` / `index` from the reference profiles the server stamps on each row.
   * Absent on older reports; the Underlying filter then falls back to the local ticker list.
   */
  assetClass?: string;
  /** Render/dedupe key: `${ticker}|${strike}|${expiry}|${side}`. */
  id: string;
  /**
   * Which report blocks named this contract.
   *
   * A contract four separate analyses flag is a stronger signal than one flagged once, and
   * that was invisible while duplicates were discarded instead of merged.
   */
  sources?: string[];
  /** HIGH | ELEVATED | MODERATE, from the daily book. */
  conviction?: string;
  /** How many times the session surfaced this contract. */
  appearances?: number;
  /** NEW on first sight; REINFORCED once the idea strengthened during the session. */
  repeatStatus?: string;
  /** Which analysis produced it — hedging, whale, and so on. */
  section?: string;
  /**
   * What the tape says this flow was: bought or sold, one leg or several, opening or not.
   * Derived from fields the row already carries — never fetched. Absent fields stay null so
   * the board can print "not available" rather than a guess.
   */
  verdict?: FlowVerdictLabels;
  /**
   * Raw source record behind this tile. Only Raw Flow rows carry one (the enriched tape print);
   * imbalance tiles leave it undefined. Used by the custom field filters and the extra columns.
   */
  raw?: Record<string, unknown>;
}

export interface ScannerReport {
  report_generated_utc: string | null;
  status: ScannerStatus;
  degraded: boolean;
  degraded_reason: string | null;
  last_success_at: string | null;
  tiles: ScannerTile[];
  warnings: string[];
}

/**
 * The only allowed moneyness derivation. `|pct| > 60%` means the spot source is
 * unreliable for that ticker, so no value is shown rather than a computed one.
 */
export function computeMoneyness(
  spot: number | null,
  strike: number,
  side: TileSide,
): { money: string; mc: MoneynessBucket } {
  if (spot === null || spot === undefined || !Number.isFinite(spot)) {
    return { money: '----', mc: 'NA' };
  }
  const pct = ((strike - spot) / spot) * 100;
  if (Math.abs(pct) > 60) {
    return { money: '----', mc: 'NA' };
  }
  const rounded = Math.round(Math.abs(pct));
  if (rounded === 0) {
    return { money: 'ATM', mc: 'ATM' };
  }
  const bucket: MoneynessBucket = side === 'C'
    ? (spot > strike ? 'ITM' : 'OTM')
    : (spot < strike ? 'ITM' : 'OTM');
  return { money: `${bucket} ${rounded}%`, mc: bucket };
}

/** The only other allowed derivation: colour = direction, not tape-side execution. */
export function computeColor(flow: TileFlow, side: TileSide): TileColor {
  if (flow !== 'BUY' && flow !== 'SELL') return 'unk';
  if ((flow === 'BUY' && side === 'C') || (flow === 'SELL' && side === 'P')) return 'buy';
  return 'sell';
}

/** Deterministic direction label — same inputs and rule as computeColor. */
export function computeFlowLabel(flow: TileFlow, side: TileSide): string {
  if (flow === 'BUY' && side === 'C') return 'Buy Call';
  if (flow === 'BUY' && side === 'P') return 'Buy Put';
  if (flow === 'SELL' && side === 'C') return 'Sell Call';
  if (flow === 'SELL' && side === 'P') return 'Sell Put';
  return 'Unknown';
}

/**
 * DTE relative to the report's own date, not "now", so the whole view reflects one
 * coherent snapshot. Returns null when either date is unparseable.
 */
export function daysBetween(reportUtcIso: string, expiryDateStr: string): number | null {
  const [ry, rm, rd] = String(reportUtcIso).slice(0, 10).split('-').map(Number);
  const [ey, em, ed] = String(expiryDateStr).slice(0, 10).split('-').map(Number);
  const reportMs = Date.UTC(ry, rm - 1, rd);
  const expiryMs = Date.UTC(ey, em - 1, ed);
  if (!Number.isFinite(reportMs) || !Number.isFinite(expiryMs)) return null;
  return Math.round((expiryMs - reportMs) / 86400000);
}

type RawContract = Record<string, unknown>;

function finiteOrNull(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? Math.round(n) : null;
}

function parseContract(
  item: RawContract,
  spotMap: Record<string, unknown> | null,
  reportUtcIso: string,
  warnings: string[],
): ScannerTile | null {
  const symbol = item && typeof item.symbol === 'string' ? item.symbol : '';
  if (!symbol.trim()) {
    warnings.push('skipped a contract: missing symbol');
    return null;
  }
  // The symbol is the fallback for everything the block does not spell out. Blocks disagree
  // about which fields they carry, and rejecting a contract for a missing `side` when the
  // symbol says CALL discards real opportunities over a formatting difference.
  const occ = parseOccSymbol(symbol);
  const ticker = occ?.ticker || symbol.trim().split(/\s+/)[0];
  if (!ticker) {
    warnings.push(`skipped contract: could not derive ticker from symbol "${symbol}"`);
    return null;
  }

  const side: TileSide | null =
    item.side === 'C' || item.side === 'P' ? (item.side as TileSide) : occ?.side ?? null;
  if (!side) {
    warnings.push(`skipped ${ticker}: no side in the row or its symbol`);
    return null;
  }

  const rawStrike = item.strike ?? occ?.strike;
  const strike = parseFloat(String(rawStrike));
  if (!Number.isFinite(strike)) {
    warnings.push(`skipped ${ticker}: no strike in the row or its symbol`);
    return null;
  }

  const expiry =
    typeof item.expiry === 'string' && item.expiry.trim() ? item.expiry.trim() : occ?.expiry || '';
  if (!expiry) {
    warnings.push(`skipped ${ticker}: no expiry in the row or its symbol`);
    return null;
  }

  const prem = typeof item.premium === 'number' && Number.isFinite(item.premium) ? item.premium : null;
  const rv = typeof item.rel_vol === 'number' && Number.isFinite(item.rel_vol) ? item.rel_vol : null;
  const horizonTml = typeof item.horizon_tml === 'string' ? item.horizon_tml : null;
  // The imbalance blocks name the aggressor `tape_aggressor_primary`; the daily book names it
  // `tape_side_resolved`. Reading only the first left every book row as Unknown -- and with it
  // the row colour and the Bought/Sold column -- for a field present on all of them.
  const tape = item.tape_aggressor_primary ?? item.tape_side_resolved;
  const flow: TileFlow = tape === 'BUY' || tape === 'SELL' ? tape : 'UNK';

  const spotRaw = spotMap && Object.prototype.hasOwnProperty.call(spotMap, ticker) ? spotMap[ticker] : null;
  // The row's own `spot_at_flow` is the fallback for a symbol the maps did not price: it is
  // the underlying as the flow printed, a source field rather than a guess.
  const spotAtFlow = item.spot_at_flow;
  const spot =
    typeof spotRaw === 'number' && Number.isFinite(spotRaw) && spotRaw > 0
      ? spotRaw
      : typeof spotAtFlow === 'number' && Number.isFinite(spotAtFlow) && spotAtFlow > 0
        ? spotAtFlow
        : null;

  const { money, mc } = computeMoneyness(spot, strike, side);

  return {
    t: ticker,
    rv,
    side,
    strike,
    exp: expiry,
    // The report date when there is one; otherwise the row's own `days_to_expiry`. Without
    // the fallback a row with no dated report had no DTE, and any Expiry limit hid it.
    dte: daysBetween(reportUtcIso, expiry) ?? finiteOrNull(item.days_to_expiry),
    prem,
    flow,
    money,
    mc,
    fl: computeFlowLabel(flow, side),
    color: computeColor(flow, side),
    horizon_tml: horizonTml,
    cap: typeof item.market_cap_bucket === 'string' ? item.market_cap_bucket : undefined,
    assetClass: typeof item.asset_class === 'string' && item.asset_class ? item.asset_class : undefined,
    sources: [],
    conviction: typeof item.conviction === 'string' ? item.conviction : undefined,
    appearances:
      typeof item.n_appearances === 'number' && Number.isFinite(item.n_appearances)
        ? item.n_appearances
        : undefined,
    repeatStatus: typeof item.repeat_status === 'string' ? item.repeat_status : undefined,
    section: typeof item.section === 'string' ? item.section : undefined,
    // Side included: a call and a put at one strike and expiry are different contracts, and
    // without it they share an id. That id is the React key and the sort tie-break, so the
    // collision stopped those rows reordering when the board was sorted.
    id: `${ticker}|${String(rawStrike)}|${expiry}|${side}`,
    // Direction and structure come free from the report: it states the aggressor and
    // renders the sale condition. Opening-vs-closing cannot be had that way -- it needs
    // session volume against open interest -- so it arrives on `flow_verdict_detail`,
    // which the server derives from the contract's own tape. Where the verified verdict
    // answers a question it wins; where it does not, the report's weaker reading stands
    // rather than blanking a field that did have an answer.
    verdict: mergeVerdicts(
      {
        ...EMPTY_FLOW_VERDICT,
        direction: directionFromTapeSide(flow),
        structure: structureFromConditionDisplay(
          (item as Record<string, unknown>).conditions_display,
        ),
      },
      verdictFromDetail((item as Record<string, unknown>).flow_verdict_detail),
    ),
  };
}

/**
 * Union of the report's spot map and the book's own, later sources winning.
 *
 * The report's `underlying_spot_map` is sniper-gated -- it covered four of the book's
 * fifty-nine tickers, which is why moneyness was blank on most tiles. The book now prices
 * every symbol it holds, and an archived session stores the map as of that day, so a past
 * session is read at its own closes rather than at today's prices.
 */
function mergeSpotMaps(...sources: unknown[]): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};
  let any = false;
  for (const src of sources) {
    if (!src || typeof src !== 'object') continue;
    for (const [k, v] of Object.entries(src as Record<string, unknown>)) {
      if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
        out[k.trim().toUpperCase()] = v;
        any = true;
      }
    }
  }
  return any ? out : null;
}

/** Verified verdict over reported verdict, field by field: a null answers nothing. */
function mergeVerdicts(weak: FlowVerdictLabels, strong: FlowVerdictLabels | null): FlowVerdictLabels {
  if (!strong) return weak;
  return {
    direction: strong.direction ?? weak.direction,
    buyPct: strong.buyPct ?? weak.buyPct,
    structure: strong.structure ?? weak.structure,
    multiPct: strong.multiPct ?? weak.multiPct,
    position: strong.position ?? weak.position,
    volOi: strong.volOi ?? weak.volOi,
    oiChange: strong.oiChange ?? weak.oiChange,
    oiDate: strong.oiDate ?? weak.oiDate,
  };
}

export function parseTiles(data: Record<string, unknown>, warnings: string[]): ScannerTile[] {
  const book = data.daily_book as Record<string, unknown> | undefined;
  const spotMap = mergeSpotMaps(data.underlying_spot_map, book?.spot_map);
  const reportUtc = String(data.report_generated_utc ?? '');
  const seen = new Map<string, ScannerTile>();

  // The daily book, when the report carries one, is the whole session's opportunities rather
  // than whatever the last cycle surfaced. Preferring it is the difference between a board
  // showing nine contracts that change every twenty minutes and one showing the hundred-odd
  // the session actually produced. The per-cycle blocks remain the fallback for a report
  // generated before the book existed.
  const bookRows = book && Array.isArray(book.opportunities) ? (book.opportunities as RawContract[]) : [];
  if (bookRows.length > 0) {
    for (const item of bookRows) {
      if (!item || typeof item !== 'object') continue;
      const tile = parseContract(item, spotMap, reportUtc, warnings);
      if (!tile) continue;
      const existing = seen.get(tile.id);
      if (existing) {
        existing.sources = existing.sources || [];
        if (!existing.sources.includes('daily_book')) existing.sources.push('daily_book');
        continue;
      }
      tile.sources = ['daily_book'];
      seen.set(tile.id, tile);
    }
    return Array.from(seen.values());
  }

  for (const key of IMBALANCE_LIST_KEYS) {
    const list = data[key];
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      if (!item || typeof item !== 'object') continue;
      const symbol = (item as RawContract).symbol;
      if (typeof symbol !== 'string') continue;

      const existing = seen.get(symbol);
      if (existing) {
        // Merged, not dropped. 105 of the 567 opportunities in a report name a contract that
        // another block also names, and the previous rule kept whichever block happened to be
        // read first and discarded the rest -- so a contract flagged by three separate
        // analyses looked identical to one flagged once. The blocks that named it are recorded,
        // and the strongest premium and relative volume survive.
        //
        // Premium is the maximum across blocks and never the sum: two blocks reporting the
        // same contract are two opinions about one trade, not two trades, and adding them
        // would invent money that was never spent.
        const merged = parseContract(item as RawContract, spotMap, reportUtc, warnings);
        if (!merged) continue;
        existing.sources = existing.sources || [];
        if (!existing.sources.includes(key)) existing.sources.push(key);
        if (merged.prem != null && (existing.prem == null || merged.prem > existing.prem)) {
          existing.prem = merged.prem;
        }
        if (merged.rv != null && (existing.rv == null || merged.rv > existing.rv)) {
          existing.rv = merged.rv;
        }
        if (existing.horizon_tml == null && merged.horizon_tml != null) {
          existing.horizon_tml = merged.horizon_tml;
        }
        continue;
      }

      const tile = parseContract(item as RawContract, spotMap, reportUtc, warnings);
      if (tile) {
        tile.sources = [key];
        seen.set(symbol, tile);
      }
    }
  }
  return Array.from(seen.values());
}

/** Returns an error string describing the first shape problem, or null when valid. */
export function validateShape(json: unknown): string | null {
  if (!json || typeof json !== 'object') return 'response is not a JSON object';
  const data = (json as Record<string, unknown>).data;
  if (!data || typeof data !== 'object') return 'missing data object';
  const d = data as Record<string, unknown>;
  if (typeof d.report_generated_utc !== 'string' || !d.report_generated_utc) {
    return 'missing data.report_generated_utc';
  }
  for (const key of REQUIRED_LIST_KEYS) {
    if (!Array.isArray(d[key])) return `missing/invalid list: ${key}`;
  }
  // The rest are optional, but must be lists when present.
  for (const key of IMBALANCE_LIST_KEYS) {
    if (key in d && !Array.isArray(d[key])) return `missing/invalid list: ${key}`;
  }
  if (!d.underlying_spot_map || typeof d.underlying_spot_map !== 'object') {
    return 'missing data.underlying_spot_map';
  }
  return null;
}

export const SCANNER_ENDPOINT = '/api/ois/report-pulse-summary-json';

export interface FetchedReport {
  report_generated_utc: string;
  tiles: ScannerTile[];
  warnings: string[];
  /** Set when the tiles came from the daily book, so the header can name the session shown. */
  book_session_date?: string;
  /** `open` while the book is still accumulating; anything else is a completed session. */
  book_market_state?: string;
}

/**
 * Tiles for an archived session's book.
 *
 * The same parser as the live path: an archived book is the identical shape, just fetched by
 * date rather than ridden along with the report. It carries `spot_map` -- the underlying
 * closes for that session -- so moneyness describes the day being shown rather than today's
 * prices, which would describe the wrong day.
 */
export function tilesFromDailyBook(
  book: Record<string, unknown> | null | undefined,
  warnings: string[],
): ScannerTile[] {
  if (!book) return [];
  // The session's own date anchors DTE, so an archived board counts days from that session.
  const sessionDate = typeof book.session_date === 'string' ? book.session_date : '';
  return parseTiles({ daily_book: book, report_generated_utc: sessionDate } as Record<string, unknown>, warnings);
}

/** Fetches and validates one report. Throws on network, HTTP or shape failure. */
export async function fetchScannerReport(timeoutMs = 15000): Promise<FetchedReport> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await elitePulseFetch(SCANNER_ENDPOINT, {
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`upstream HTTP ${res.status}`);
    const json = await res.json();
    const shapeError = validateShape(json);
    if (shapeError) throw new Error(`unexpected response shape: ${shapeError}`);
    const data = (json as { data: Record<string, unknown> }).data;
    const warnings: string[] = [];
    const book = data.daily_book as Record<string, unknown> | undefined;
    return {
      report_generated_utc: String(data.report_generated_utc),
      tiles: parseTiles(data, warnings),
      warnings,
      book_session_date: book ? String(book.session_date || '') : undefined,
      book_market_state: book ? String(book.market_state || '') : undefined,
    };
  } finally {
    clearTimeout(timer);
  }
}

// ── last-good cache ───────────────────────────────────────────────────────────
// Browser-side equivalent of the RTL-scanner `last-good.json` on-disk cache: the last
// successfully fetched AND validated report, so a reload can render immediately and a
// failed poll degrades to STALE instead of a blank screen. Only ever written after a
// successful validation — never on failure.

const LAST_GOOD_KEY = 'rtlScannerLastGood';

/**
 * Bumped whenever a tile gains a field. A cache written before the field existed holds
 * tiles with it absent, and restoring those renders a board with blank columns that the
 * current report would have filled -- indistinguishable, to the reader, from the data
 * genuinely being missing. An entry stamped with anything else is discarded.
 */
const TILE_SCHEMA_VERSION = 4;

export interface LastGood {
  report_generated_utc: string;
  tiles: ScannerTile[];
  last_success_at: string;
  schema?: number;
}

export function loadLastGood(): LastGood | null {
  try {
    const raw = window.localStorage.getItem(LAST_GOOD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.report_generated_utc !== 'string' || !Array.isArray(parsed.tiles)) return null;
    if (parsed.schema !== TILE_SCHEMA_VERSION) return null;
    return parsed as LastGood;
  } catch {
    return null;
  }
}

export function saveLastGood(value: LastGood): void {
  try {
    window.localStorage.setItem(
      LAST_GOOD_KEY,
      JSON.stringify({ ...value, schema: TILE_SCHEMA_VERSION }),
    );
  } catch {
    // Storage full or unavailable — the in-memory copy still serves this session.
  }
}
